<template>
  <div class="d-flex flex-column gap-2">
    <template v-if="verificationStep === 'IDLE' && !loading">
      <h6 class="mb-0">Confirme os dados da solicitação</h6>

      <v-list dense>
        <v-list-item>
          <v-list-item-avatar class="mr-2">
            <v-icon>mdi-account</v-icon>
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-subtitle>Nome do solicitante</v-list-item-subtitle>
            <v-list-item-title>{{ user?.name }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-item>
          <v-list-item-avatar class="mr-2">
            <v-icon>mdi-email</v-icon>
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-subtitle>E-mail do solicitante</v-list-item-subtitle>
            <v-list-item-title>{{ user?.email }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-item>
          <v-list-item-avatar class="mr-2">
            <v-icon>mdi-certificate</v-icon>
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-subtitle>Tipo de benefício</v-list-item-subtitle>
            <v-list-item-title>
              {{ concessionTypes[value?.type]?.name }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-item>
          <v-list-item-avatar class="mr-2">
            <v-icon>mdi-file</v-icon>
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-subtitle>Documentos enviados</v-list-item-subtitle>
            <v-list-item-title>{{
              [
                value.files?.documentFront?.file?.name,
                value.files?.documentBack?.file?.name,
              ]
                .filter(Boolean)
                .join(", ")
            }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <!-- <v-list-item>
          <v-list-item-avatar class="mr-2">
            <v-icon>mdi-check-circle</v-icon>
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-subtitle>
              Autoriza análise automatizada
            </v-list-item-subtitle>
            <v-list-item-title>{{
              value.allowAi ? "Sim" : "Não"
            }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item> -->
      </v-list>
      <v-btn text @click="$emit('back')" small> Alterar dados </v-btn>
      <v-checkbox
        v-model="confirm"
        label="Confirmo que os dados estão corretos"
        class="mt-0 pt-0 ml-2"
      />

      <v-alert v-if="error" type="error" class="mt-2">
        {{ error }}
      </v-alert>

      <v-btn color="primary" @click="save" :disabled="!confirm">
        Enviar solicitação
      </v-btn>
    </template>
    <template v-else>
      <v-scroll-y-transition leave-absolute hide-on-leave>
        <div
          :key="verificationStep"
          class="d-flex flex-column gap-2 text-center justify-center px-2 pt-4"
          style="height: 270px"
        >
          <v-icon size="60" :color="verificationSteps[verificationStep]?.iconColor">
            {{ verificationSteps[verificationStep].icon }}
          </v-icon>
          <h5 class="mb-0">
            {{ verificationSteps[verificationStep].title }}
          </h5>
          <p
            v-if="verificationSteps[verificationStep].description"
            style="text-wrap: balance"
          >
            {{ verificationSteps[verificationStep].description }}
          </p>
          <v-btn
            v-if="verificationStep === 'SUCCESS'"
            text
            small
            color="primary"
            @click="$emit('finish')"
          >
            Ver solicitação
          </v-btn>
        </div>
      </v-scroll-y-transition>
    </template>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import concessionTypes from "@/definitions/concessionTypes.json";
import ACCOUNT from "@/services/account";

export default {
  name: "ConcessionSaveForm",
  emits: ["input"],
  props: {
    value: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      concessionTypes,
      confirm: false,
      loading: false,
      error: null,
      verificationStep: "IDLE",
      verificationSteps: {
        IDLE: {
          icon: "mdi-shield-check",
          title: "Confirme os dados da solicitação",
          description:
            "Confira os dados da solicitação para garantir que tudo está correto.",
        },
        FILES: {
          icon: "mdi-file-check",
          title: "Preparando arquivos",
          description: "Preparando os arquivos da solicitação para envio.",
        },
        SUBMIT: {
          icon: "mdi-send",
          title: "Enviando solicitação",
          description: "Enviando a solicitação para análise.",
        },
        SUCCESS: {
          icon: "mdi-check-circle",
          iconColor: "success",
          title: "Solicitação enviada com sucesso",
          description:
            "Quando o resultado da solicitação estiver disponível, você receberá um e-mail com o resultado.",
        },
      },
    };
  },
  methods: {
    async save() {
      try {
        this.loading = true;
        this.error = null;

        this.verificationStep = "FILES";
        const data = {
          type: this.value.type,
          allowAi: !!this.value?.allowAi,
        };
        const { concession } = await ACCOUNT.concession.create(data);
        await new Promise((resolve) => setTimeout(resolve, 1000));

        this.verificationStep = "SUBMIT";
        if (this.value.files?.documentFront?.file)
          await this.uploadFile(
            concession.id,
            this.value.files.documentFront.file,
            "documentFront"
          );

        if (this.value.files?.documentBack?.file)
          await this.uploadFile(
            concession.id,
            this.value.files.documentBack.file,
            "documentBack"
          );

        await new Promise((resolve) => setTimeout(resolve, 500));

        await ACCOUNT.concession.submit(concession.id);

        this.verificationStep = "SUCCESS";
        this.$emit("success");
      } catch (e) {
        this.verificationStep = "IDLE";
        this.error =
          e.message ||
          "Ocorreu um erro ao salvar a solicitação, tente novamente mais tarde";
      } finally {
        this.loading = false;
      }
    },
    async uploadFile(concessionId, file, type) {
      if (!file) return null;
      const response = await ACCOUNT.concession.uploadFile(
        concessionId,
        file,
        type
      );
      return response;
    },
  },
  computed: {
    ...mapGetters("auth", ["user"]),
    selectedType: {
      get() {
        return this.value;
      },
      set(value) {
        console.log(value);
        this.$emit("input", value);
      },
    },
  },
};
</script>
